import {
  BALANCE_CODIGO_OTROS_ACTIVOS_FIN,
  BALANCE_CODIGO_OTROS_PASIVOS_FIN,
  BALANCE_KEY_TOTAL_ACTIVO,
  BALANCE_KEY_TOTAL_PN_PASIVO,
  BALANCE_NPGC_ESTRUCTURA,
  balanceIndentLevel,
  isActivoCodigo,
  isBalanceDescendant,
  isBalanceSectionCodigo,
} from "../../constants/balanceNpgcEstructura";
import { MESES, buildExcelRows } from "../PyGAnalitico/pygAnaliticoBuild";

export { MESES, buildExcelRows };

const rowCuenta = (r) =>
  String(r?.Cuenta6 ?? r?.cuenta6 ?? r?.CUENTA ?? r?.cuenta ?? "").trim();

const rowCodigo = (r) => String(r?.CODIGO ?? r?.codigo ?? "").trim();

const EPS = 0.005;

const mesHasAmount = (mes) => {
  for (let m = 1; m <= 12; m += 1) {
    if (Math.abs(Number(mes?.[m] || 0)) > EPS) return true;
  }
  return false;
};

/** YTD de cada cuenta (suma de movimientos del año) para clasificar 551. */
export const accountYtdByCuenta = (rawData) => {
  const ytd = new Map();
  (rawData || []).forEach((r) => {
    const cuenta = rowCuenta(r);
    if (!cuenta) return;
    ytd.set(
      cuenta,
      (ytd.get(cuenta) || 0) + (Number(r.Saldo ?? r.saldo ?? 0) || 0),
    );
  });
  return ytd;
};

export const remapCodigoByYtd = (codigo, cuenta, ytd) => {
  if (!String(cuenta || "").startsWith("551")) return codigo;
  const n = Number(ytd) || 0;
  if (n > EPS) return BALANCE_CODIGO_OTROS_PASIVOS_FIN;
  if (n < -EPS) return BALANCE_CODIGO_OTROS_ACTIVOS_FIN;
  return codigo;
};

/**
 * A3 Balance de situación: 551 con saldo acreedor (YTD > 0 en la vista NPGC)
 * va a "Otros pasivos financieros", no a activo.
 */
export const remapBalanceRowCodigo = (row, ytdByCuenta) => {
  const codigo = rowCodigo(row);
  const cuenta = rowCuenta(row);
  return remapCodigoByYtd(codigo, cuenta, ytdByCuenta?.get(cuenta) || 0);
};

export const presentBalanceRawData = (rawData) => {
  const ytd = accountYtdByCuenta(rawData);
  return (rawData || []).map((r) => ({
    ...r,
    CODIGO: remapBalanceRowCodigo(r, ytd),
  }));
};

const emptyMes = () => {
  const mes = {};
  for (let m = 1; m <= 12; m += 1) mes[m] = 0;
  return mes;
};

export const clampBalanceMonth = (month) => {
  const n = Number(month);
  if (!Number.isFinite(n)) return 12;
  return Math.min(12, Math.max(1, Math.round(n)));
};

/** @deprecated columnas = todos los cierres; el periodo solo afecta al Total */
export const monthsForPeriod = (throughMonth = 12) =>
  MESES.slice(0, clampBalanceMonth(throughMonth));

/** Meses visibles: el año entero, o hasta hoy si es el año en curso. */
export const monthsForBalanceView = (year, now = new Date()) => {
  const y = Number(year);
  if (y === now.getFullYear()) return MESES.slice(0, now.getMonth() + 1);
  if (y > now.getFullYear()) return MESES.slice(0, 1);
  return [...MESES];
};

export const defaultBalanceThroughMonth = (year, now = new Date()) => {
  const y = Number(year);
  if (y === now.getFullYear()) return now.getMonth() + 1;
  if (y > now.getFullYear()) return 1;
  return 12;
};

export const normalizeFechaCierreIso = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime()) || value.getFullYear() < 1990) {
      return null;
    }
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || Number(s.slice(0, 4)) < 1990) {
    return null;
  }
  return s;
};

export const monthEndIso = (year, month) => {
  const d = new Date(Number(year), Number(month), 0);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

/** closed | open | unknown — según FECHACIERRE de A3 (TIPOSCON). */
export const balanceMonthStatus = (year, month, fechaCierreIso) => {
  const close = normalizeFechaCierreIso(fechaCierreIso);
  if (!close) return "unknown";
  return monthEndIso(year, month) <= close ? "closed" : "open";
};

export const parseBalanceNpgcResponse = (data) => {
  if (Array.isArray(data)) {
    return { rows: data, fechaCierre: null };
  }
  return {
    rows: Array.isArray(data?.rows) ? data.rows : [],
    fechaCierre: normalizeFechaCierreIso(data?.fechaCierre),
  };
};

export const filterBalanceRowsThroughMonth = (rawData, throughMonth = 12) => {
  const through = clampBalanceMonth(throughMonth);
  return (rawData || []).filter((r) => {
    const mesVal = Number(r.Mes ?? r.mes);
    return mesVal >= 1 && mesVal <= through;
  });
};

const addMes = (target, source) => {
  for (let m = 1; m <= 12; m += 1) {
    target[m] = (target[m] || 0) + (source[m] || 0);
  }
};

/**
 * Movimiento mensual → saldo de cierre de cada mes (01/01 → fin de m).
 * throughMonth solo elige qué cierre va en `total`; no borra meses posteriores.
 */
export const toYtdMes = (mes, throughMonth = 12) => {
  const through = clampBalanceMonth(throughMonth);
  const out = emptyMes();
  let run = 0;
  for (let m = 1; m <= 12; m += 1) {
    run += Number(mes?.[m] || 0);
    out[m] = run;
  }
  return { mes: out, total: out[through] };
};

const ensureGroup = (map, order, codigo, titulo) => {
  if (map[codigo]) return map[codigo];
  const g = {
    codigo,
    titulo: titulo || codigo,
    indent: balanceIndentLevel(codigo),
    isSection: isBalanceSectionCodigo(codigo),
    mes: emptyMes(),
    total: 0,
    subGroupOrder: ["__root__"],
    subGroups: {
      __root__: {
        title: null,
        accounts: {},
        accountList: [],
        mes: emptyMes(),
        total: 0,
      },
    },
    _calculated: false,
  };
  map[codigo] = g;
  order.push(codigo);
  return g;
};

const applyPeriodTotal = (g, throughMonth = 12) => {
  const through = clampBalanceMonth(throughMonth);
  g.total = Number(g.mes?.[through] || 0);
  (g.subGroupOrder || []).forEach((sk) => {
    const sub = g.subGroups[sk];
    if (!sub) return;
    sub.total = Number(sub.mes?.[through] || 0);
    (sub.accountList || []).forEach((acc) => {
      acc.total = Number(acc.mes?.[through] || 0);
    });
  });
};

const negateMes = (mes) => {
  const out = emptyMes();
  for (let m = 1; m <= 12; m += 1) {
    const n = Number(mes?.[m]) || 0;
    out[m] = n === 0 ? 0 : -n;
  }
  return out;
};

const flipSign = (n) => {
  const x = -Number(n || 0);
  return x === 0 ? 0 : x;
};

/** Vista NPGC: activo en negativo (debe). A3 lo pinta positivo. */
const negateGroupPresentation = (g) => {
  g.mes = negateMes(g.mes);
  g.total = flipSign(g.total);
  (g.subGroupOrder || []).forEach((sk) => {
    const sub = g.subGroups[sk];
    if (!sub) return;
    sub.mes = negateMes(sub.mes);
    sub.total = flipSign(sub.total);
    (sub.accountList || []).forEach((acc) => {
      acc.mes = negateMes(acc.mes);
      acc.total = flipSign(acc.total);
    });
  });
};

const collectAccountMovements = (rawData) => {
  const accounts = new Map();
  (rawData || []).forEach((r) => {
    const codigo = rowCodigo(r);
    const cuenta = rowCuenta(r);
    if (!codigo && !cuenta) return;
    const mesVal = Number(r.Mes ?? r.mes);
    const saldo = Number(r.Saldo ?? r.saldo ?? 0) || 0;
    if (!(mesVal >= 1 && mesVal <= 12)) return;
    const key = cuenta || `${codigo}#${String(r.DescripcionCuenta || "")}`;
    if (!accounts.has(key)) {
      accounts.set(key, {
        cuenta,
        codigo,
        titulo: String(r.Titulo ?? r.titulo ?? "").trim(),
        desc: String(
          r.DescripcionCuenta ?? r.descripcionCuenta ?? cuenta ?? codigo,
        ).trim(),
        mes: emptyMes(),
      });
    }
    const acc = accounts.get(key);
    if (codigo) acc.codigo = codigo;
    acc.mes[mesVal] = (acc.mes[mesVal] || 0) + saldo;
  });
  return accounts;
};

/**
 * Balance NPGC: cada mes es el saldo de cierre a fin de ese mes (independiente).
 * throughMonth solo rellena `total` y los KPI (como A3 a esa fecha).
 */
export const buildBalanceAnaliticoStructure = (
  rawData,
  estructura = BALANCE_NPGC_ESTRUCTURA,
  { throughMonth = 12 } = {},
) => {
  const through = clampBalanceMonth(throughMonth);
  const byCodigo = new Map(estructura.map((row) => [row.codigo, row.titulo]));
  const map = {};
  const order = [];
  const leafCodigos = [];
  const leafSet = new Set();

  const addClosing = (codigo, tituloHint, cuenta6, desc, month, closing) => {
    if (!codigo) return;
    if (Math.abs(Number(closing) || 0) <= EPS) return;
    const titulo =
      byCodigo.get(codigo) || String(tituloHint || "").trim() || codigo;
    const g = ensureGroup(map, order, codigo, titulo);
    if (!leafSet.has(codigo)) {
      leafSet.add(codigo);
      leafCodigos.push(codigo);
    }
    g.mes[month] = (g.mes[month] || 0) + closing;
    const sub = g.subGroups.__root__;
    sub.mes[month] = (sub.mes[month] || 0) + closing;
    if (cuenta6) {
      if (!sub.accounts[cuenta6]) {
        sub.accounts[cuenta6] = {
          codigo: cuenta6,
          titulo: desc,
          mes: emptyMes(),
          total: 0,
        };
      }
      sub.accounts[cuenta6].mes[month] =
        (sub.accounts[cuenta6].mes[month] || 0) + closing;
    }
  };

  collectAccountMovements(rawData).forEach((acc) => {
    const { mes: closings } = toYtdMes(acc.mes, 12);
    for (let m = 1; m <= 12; m += 1) {
      const codigoM = remapCodigoByYtd(acc.codigo, acc.cuenta, closings[m]);
      addClosing(codigoM, acc.titulo, acc.cuenta, acc.desc, m, closings[m]);
    }
  });

  leafCodigos.forEach((codigo) => {
    let parentProbe = codigo;
    while (parentProbe) {
      const dot = parentProbe.lastIndexOf(".");
      let parent = null;
      if (dot > 0) parent = parentProbe.slice(0, dot);
      else if (/^PA[123]$/.test(parentProbe)) parent = "PA";
      else parent = null;
      if (!parent) break;
      ensureGroup(map, order, parent, byCodigo.get(parent) || parent);
      parentProbe = parent;
    }
  });

  Object.keys(map).forEach((codigo) => {
    const g = map[codigo];
    const sub = g.subGroups.__root__;
    sub.accountList = Object.values(sub.accounts).sort((a, b) =>
      (a.codigo || "").localeCompare(b.codigo || "", undefined, {
        numeric: true,
      }),
    );
  });

  const leafMes = {};
  leafCodigos.forEach((codigo) => {
    leafMes[codigo] = { mes: { ...map[codigo].mes } };
  });

  Object.keys(map).forEach((parent) => {
    const g = map[parent];
    const own = leafMes[parent] || { mes: emptyMes() };
    g.mes = { ...own.mes };
    Object.keys(leafMes).forEach((child) => {
      if (child === parent) return;
      if (!isBalanceDescendant(parent, child)) return;
      addMes(g.mes, leafMes[child].mes);
    });
    const sub = g.subGroups.__root__;
    if (sub && !leafMes[parent]) {
      sub.mes = { ...g.mes };
    }
  });

  const allCodigos = Object.keys(map);
  allCodigos.forEach((codigo) => applyPeriodTotal(map[codigo], through));
  allCodigos.forEach((codigo) => {
    if (isActivoCodigo(codigo)) negateGroupPresentation(map[codigo]);
  });
  allCodigos.forEach((codigo) => {
    const sub = map[codigo]?.subGroups?.__root__;
    if (!sub) return;
    sub.accountList = (sub.accountList || []).filter((acc) =>
      mesHasAmount(acc.mes),
    );
  });

  const visible = allCodigos.filter((codigo) => {
    const g = map[codigo];
    const hasAmount = mesHasAmount(g.mes);
    const hasAccounts = (g.subGroups.__root__.accountList || []).some((acc) =>
      mesHasAmount(acc.mes),
    );
    const hasDesc = allCodigos.some(
      (c) => isBalanceDescendant(codigo, c) && mesHasAmount(map[c].mes),
    );
    return hasAmount || hasAccounts || hasDesc;
  });

  visible.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const sumPrefijos = (prefixes) => {
    const mes = emptyMes();
    visible.forEach((codigo) => {
      if (!prefixes.includes(codigo)) return;
      addMes(mes, map[codigo].mes);
    });
    return { mes, total: Number(mes[through] || 0) };
  };

  const totalActivo = sumPrefijos(["AA", "AB"]);
  const totalPnPasivo = sumPrefijos(["PA", "PB", "PC"]);

  const putCalculated = (key, values, afterCodigo) => {
    map[key] = {
      codigo: key,
      titulo: key,
      indent: 0,
      isSection: true,
      mes: values.mes,
      total: values.total,
      subGroupOrder: [],
      subGroups: {},
      _calculated: true,
    };
    const idx = visible.indexOf(afterCodigo);
    if (idx >= 0) visible.splice(idx + 1, 0, key);
    else visible.push(key);
  };

  const lastActivo = [...visible]
    .reverse()
    .find((codigo) => isActivoCodigo(codigo));
  const lastPnPasivo = [...visible].reverse().find((codigo) => {
    const c = String(codigo || "");
    return (
      c === "PA" ||
      c === "PB" ||
      c === "PC" ||
      c.startsWith("PA") ||
      c.startsWith("PB") ||
      c.startsWith("PC.")
    );
  });
  putCalculated(BALANCE_KEY_TOTAL_ACTIVO, totalActivo, lastActivo);
  putCalculated(BALANCE_KEY_TOTAL_PN_PASIVO, totalPnPasivo, lastPnPasivo);

  return {
    groupOrder: visible,
    groupMap: map,
    totalActivo: totalActivo.total,
    totalPnPasivo: totalPnPasivo.total,
    cuadre: totalActivo.total - totalPnPasivo.total,
  };
};

export const balanceDisplayTitle = (g) => {
  const pad = "  ".repeat(g?.indent || 0);
  return `${pad}${g?.titulo || g?.codigo || ""}`;
};
